import React, { useState, useEffect } from "react";
import { Link, useParams } from 'react-router-dom';
import { useActiveLeague } from '../hooks/useActiveLeague';
import { findObjectByKeyValue } from '../components/utils.js';
import { TeamPageNav } from "../components/team-page/TeamPageNav";
import { TeamDropdown } from "../components/common/Dropdown.jsx";
import { TableHead } from "../components/team-list/TableHead";
import { PlayerItems } from "../components/team-list/PlayerItems";
import { Spinner } from "../components/common/Spinner"; // Import Spinner
import { ErrorMessage } from "../components/common/ErrorMessage"; // Import ErrorMessage
import { toast } from 'react-hot-toast'; // Import toast
import { Popup } from "../components/common/Popup.jsx";
import TutorialTooltip from '../components/common/TutorialTooltip';

// Import API hooks
import { useGetTeams, useGetTeamById, useGetTeamsheet, useSaveTeamsheet } from "../api/teamsApi.js";
import { useGetTeamPlayers } from "../api/playersApi.js"; // Import hook for players

// Import static JSON data for formations, consistent with TeamPage.jsx
import formationsData from "../json-data/field-positions.json";
import tacticData from "../json-data/tactic.json"; // Import tactic data

function MyTeamList() {
    const { activeLeague } = useActiveLeague();
    const { id: myTeamId } = useParams();

    // Fetch data using API hooks (league-scoped)
    const leagueId = activeLeague?._id ?? null;
    const { data: teamsData, isLoading: isTeamsLoading, error: teamsError } = useGetTeams(!!leagueId, leagueId);
    const { data: teamData, isLoading: isTeamLoading, error: teamError } = useGetTeamById(myTeamId);
    // Fetch players using the hook
    const { data: playersApiResponse, isLoading: isPlayersLoading, error: playersError } = useGetTeamPlayers(
        myTeamId,
        { leagueId: activeLeague?._id, season: activeLeague?.season }
    );
    // Fetch the teamsheet data
    const { 
        data: teamSheetResponse, 
        isLoading: isTeamSheetLoading, 
        error: teamSheetError 
    } = useGetTeamsheet(myTeamId);
    // Instantiate mutation hook for saving
    const { mutate: saveTeamsheetMutate, isLoading: isSavingTeamsheet, error: saveError } = useSaveTeamsheet();

    const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
    // Local state for player list, derived from API response
    const [localPlayersList, setLocalPlayersList] = useState([]);
    // State for players in starting XI slots: Map of { slotId: wyId }
    const [assignedSlotsMap, setAssignedSlotsMap] = useState({});
    // State for players assigned as substitutes: Map of { wyId: positionLabel }
    const [assignedSubstitutesMap, setAssignedSubstitutesMap] = useState({});
    const [activeFormationId, setActiveFormationId] = useState(null);
    const [currentFormationSortMap, setCurrentFormationSortMap] = useState({});
    // State holds ALL available slots (XI + Subs + Remove) for the dropdown
    const [currentFormationSlots, setCurrentFormationSlots] = useState([{ position: '-', display: 'Remove from Teamsheet' }]);
    // Add state for tactic selection
    const [activeTacticId, setActiveTacticId] = useState(null);
    const [activeSettings, setActiveSettings] = useState(false);
    const [playerForPositionChange, setPlayerForPositionChange] = useState(null);

    // Ref to track if initialization from teamsheet has occurred
    const isInitializedRef = React.useRef(false);

    // Initialize state from teamsheet (run only once when data arrives)
    useEffect(() => {
        const sheetData = teamSheetResponse?.data;

        // Only run initialization if sheetData exists and we haven't initialized yet
        if (sheetData && !isInitializedRef.current) {
            console.log("[Init Effect - ONCE] Running initialization.");
            isInitializedRef.current = true; // Mark as initialized

            const availableFormations = formationsData || [];
            const availableTactics = tacticData || [];
            let initialActiveFormationId = availableFormations[0]?.id || null;
            let initialActiveTacticId = null; // Default tactic ID
            let selectedFormationForInit = null;

            console.log("[Init Effect] Starting. Sheet data:", sheetData);

            // Determine initial formation ID (same logic as before)
            if (sheetData?.formation) {
                const foundFormation = availableFormations.find(f => f.title === sheetData.formation);
                if (foundFormation) {
                    initialActiveFormationId = foundFormation.id;
                    selectedFormationForInit = foundFormation;
                    console.log(`[Init Effect] Found formation in sheet: ${foundFormation.title} (ID: ${initialActiveFormationId})`);
                } else {
                    initialActiveFormationId = availableFormations[0]?.id || null; // Fallback ID
                    selectedFormationForInit = availableFormations.find(f => f.id === initialActiveFormationId); // Get fallback formation
                    console.warn(`[Init Effect] Sheet formation '${sheetData.formation}' not found, using default: ${selectedFormationForInit?.title}`);
                }
            } else {
                 initialActiveFormationId = availableFormations[0]?.id || null; // Fallback ID
                 selectedFormationForInit = availableFormations.find(f => f.id === initialActiveFormationId); // Get fallback formation
                console.log(`[Init Effect] No sheet formation, using default ID: ${initialActiveFormationId} -> ${selectedFormationForInit?.title}`);
            }
            // Only set state if the ID actually changed
            if (initialActiveFormationId !== activeFormationId) {
                setActiveFormationId(initialActiveFormationId);
            }

            // Initialize Active Tactic ID (same logic as before)
            if (sheetData?.tacticId) {
                const tacticIdReverseMapping = {
                    "A": 1, "N": 2, "D": 3, "C": 4, "P": 5, "L": 6
                };
                const mappedTacticId = tacticIdReverseMapping[sheetData.tacticId];
                if (mappedTacticId) {
                    initialActiveTacticId = mappedTacticId;
                    console.log(`[Init Effect] Found tactic in sheet: ${sheetData.tacticId} -> ID: ${initialActiveTacticId}`);
                } else {
                    initialActiveTacticId = availableTactics[0]?.id || null; // Fallback to first tactic
                    console.warn(`[Init Effect] Sheet tactic ID '${sheetData.tacticId}' not found in mapping, using default: ${initialActiveTacticId}`);
                }
            } else {
                initialActiveTacticId = availableTactics[0]?.id || null; // Default if not in sheet
                 console.log(`[Init Effect] No sheet tactic, using default ID: ${initialActiveTacticId}`);
            }
            // Only set state if the ID actually changed
             if (initialActiveTacticId !== activeTacticId) {
                 setActiveTacticId(initialActiveTacticId);
             }

            // Initialize assignedSlotsMap (Starting XI)
            const newSlotsMap = {};
            if (sheetData?.players && selectedFormationForInit?.positions) {
                const availableSlots = [...selectedFormationForInit.positions]; // Copy available slots
                console.log(`[Init Effect] Mapping ${sheetData.players.length} XI players from sheet to ${availableSlots.length} slots.`);

                sheetData.players.forEach(player => {
                    if (player.wyId == null || !player.position) return; // Skip invalid players

                    // Find the first available slot matching the player's position label
                    // Use findIndex and splice to ensure a slot isn't assigned twice
                    const matchingSlotIndex = availableSlots.findIndex(slot => slot.position === player.position);

                    if (matchingSlotIndex !== -1) {
                        const assignedSlot = availableSlots.splice(matchingSlotIndex, 1)[0]; // Find and remove slot
                        newSlotsMap[assignedSlot.id] = player.wyId;
                        console.log(`[Init Effect] Mapped XI player ${player.wyId} to slot ${assignedSlot.id} (Pos: ${assignedSlot.position})`);
                    } else {
                        console.warn(`[Init Effect] No available slot found for XI player ${player.wyId} with position ${player.position}`);
                    }
                });
            }
            // Only set state if the map actually changed
            if (JSON.stringify(newSlotsMap) !== JSON.stringify(assignedSlotsMap)) {
                setAssignedSlotsMap(newSlotsMap);
            }
            console.log("[Init Effect - ONCE] Final initial assignedSlotsMap:", newSlotsMap);

            // Initialize assignedSubstitutesMap (Subs)
            const newSubsMap = {};
            if (sheetData?.subs && Array.isArray(sheetData.subs)) {
                console.log(`[Init Effect] Mapping ${sheetData.subs.length} Substitute players from sheet.`);
                sheetData.subs.forEach(sub => {
                    // Check if essential data exists, but DON'T require the prefix from DB
                    if (sub.wyId != null && sub.position) { 
                         // Add the prefix when storing in the component's state map
                         const positionWithPrefix = `SUB - ${sub.position}`;
                         newSubsMap[sub.wyId] = positionWithPrefix; 
                         console.log(`[Init Effect] Mapped Substitute player ${sub.wyId} to internal position ${positionWithPrefix} (from DB position ${sub.position})`);
                    } else {
                         // Log if data is incomplete
                         console.warn(`[Init Effect] Invalid or incomplete substitute data found in sheet:`, sub);
                    }
                });
            }
            // Only set state if the map actually changed
            if (JSON.stringify(newSubsMap) !== JSON.stringify(assignedSubstitutesMap)) {
                 setAssignedSubstitutesMap(newSubsMap);
            }
            console.log("[Init Effect - ONCE] Final initial assignedSubstitutesMap:", newSubsMap);

            // Also calculate and set initial formation slots and sort map here
            if (selectedFormationForInit && selectedFormationForInit.positions) {
                const initialSortMap = selectedFormationForInit.positions.reduce((map, posObj) => {
                    if (posObj.position && posObj.sortOrder != null) {
                        map[posObj.position] = posObj.sortOrder;
                    }
                    return map;
                }, {});
                setCurrentFormationSortMap(initialSortMap);

                const formationSlotObjects = [...selectedFormationForInit.positions];
                const substituteSlotObjects = formationSlotObjects
                    .map(slot => ({
                        // No ID for subs, rely on position label
                        position: `SUB - ${slot.position}`,
                        display: `SUB - ${slot.position}`,
                        sortOrder: (slot.sortOrder ?? 99) + 100
                    }));
                const allSlots = [...formationSlotObjects, ...substituteSlotObjects];
                allSlots.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
                const finalSlotsForDropdown = [
                    { position: '-', display: 'Remove from Teamsheet' },
                    ...allSlots
                ];
                 // Only set state if the slots actually changed
                 if (JSON.stringify(finalSlotsForDropdown) !== JSON.stringify(currentFormationSlots)) {
                     setCurrentFormationSlots(finalSlotsForDropdown);
                 }
                 // Only set state if the sort map actually changed
                 if (JSON.stringify(initialSortMap) !== JSON.stringify(currentFormationSortMap)) {
                     setCurrentFormationSortMap(initialSortMap);
                 }
                console.log("[Init Effect - ONCE] Set initial currentFormationSlots:", finalSlotsForDropdown);
            } else {
                 // Reset if formation invalid
                 setCurrentFormationSortMap({});
                 setCurrentFormationSlots([{ position: '-', display: 'Remove from Teamsheet' }]);
            }

        } else if (sheetData && isInitializedRef.current) {
            console.log("[Init Effect] Skipping initialization, already done.");
        } else if (!sheetData && !teamSheetError && !isTeamSheetLoading) {
             // Handle case where teamsheet is explicitly null/empty on load (and not loading/error)
             console.log("[Init Effect] No teamsheet data found, initializing with defaults.");
             isInitializedRef.current = true; // Mark as initialized to prevent re-running

             // Set default formation/tactic if needed (using first available)
             const availableFormations = formationsData || [];
             const availableTactics = tacticData || [];
             if (!activeFormationId && availableFormations.length > 0) {
                 setActiveFormationId(availableFormations[0].id);
                 console.log(`[Init Effect] Setting default formation ID: ${availableFormations[0].id}`);
             }
             if (!activeTacticId && availableTactics.length > 0) {
                  setActiveTacticId(availableTactics[0].id);
                  console.log(`[Init Effect] Setting default tactic ID: ${availableTactics[0].id}`);
             }
             // Ensure maps are empty
             setAssignedSlotsMap({});
             setAssignedSubstitutesMap({});

        } else {
            console.log("[Init Effect] Skipping initialization (loading, error, or no data yet).");
        }

    // Depend on the fetched teamsheet data object reference, error, and loading state
    }, [teamSheetResponse, isTeamSheetLoading, teamSheetError]); 

    // Effect to update the sort map AND the list of available position SLOTS for the dropdown
    useEffect(() => {
        // Prevent running if activeFormationId is not set yet
        if (activeFormationId === null) {
             console.log("[Formation Slots Effect] Skipping, activeFormationId is null.");
             return;
        }
        console.log(`[TeamPageList] Updating sort map & position SLOTS for formation ID: ${activeFormationId}`);
        const selectedFormation = findObjectByKeyValue(formationsData || [], 'id', activeFormationId);

        if (selectedFormation && selectedFormation.positions) {
            // Update Sort Map (for starting XI positions)
            const newSortMap = selectedFormation.positions.reduce((map, posObj) => {
                if (posObj.position && posObj.sortOrder != null) {
                    map[posObj.position] = posObj.sortOrder;
                }
                return map;
            }, {});
            setCurrentFormationSortMap(newSortMap); // Still used for sorting the player list

            // Generate list of starting XI Slot Objects from the formation
            const formationSlotObjects = [...selectedFormation.positions]; // Copy

            // Generate list of Substitute Slot Objects
            const substituteSlotObjects = formationSlotObjects
                .map(slot => ({
                    // No ID for subs, rely on position label
                    position: `SUB - ${slot.position}`,
                    display: `SUB - ${slot.position}`,
                    sortOrder: (slot.sortOrder ?? 99) + 100
                }));

            // Combine and Sort all available slot objects (XI + Subs)
            const allSlots = [...formationSlotObjects, ...substituteSlotObjects];
            allSlots.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));

            // Set the state for the dropdown, ensuring 'Remove' is always first
            const finalSlotsForDropdown = [
                { position: '-', display: 'Remove from Teamsheet' }, // Special object for remove
                ...allSlots // Add sorted XI and SUB slots
            ];
            setCurrentFormationSlots(finalSlotsForDropdown);
            console.log("[TeamPageList] New formation slots (XI + Subs):", finalSlotsForDropdown);

        } else {
            console.warn(`[TeamPageList] Could not find formation or positions for ID ${activeFormationId}. Resetting.`);
            setCurrentFormationSortMap({});
            setCurrentFormationSlots([{ position: '-', display: 'Remove from Teamsheet' }]); // Reset to default
        }
    }, [activeFormationId]); // Dependency remains activeFormationId

    // Merge player list - Update tacticalPosition lookup using both maps
    useEffect(() => {
        if (playersApiResponse?.data) {
            const fullPlayerList = playersApiResponse.data;
             // Need the list of *all* slots (XI, Sub, Remove) to look up labels potentially
            const allAvailableSlotsForLookup = currentFormationSlots;

            const processedPlayers = fullPlayerList.map(p => {
                let assignedPosLabel = '-'; // Default to unassigned
                const playerWyId = p.wyId;

                // 1. Check if player is assigned as a substitute
                if (assignedSubstitutesMap[playerWyId]) {
                    assignedPosLabel = assignedSubstitutesMap[playerWyId];
                } else {
                    // 2. If not a sub, check if player is in the starting XI
                    const assignedSlotId = Object.keys(assignedSlotsMap).find(slotId => assignedSlotsMap[slotId] === playerWyId);
                    if (assignedSlotId) {
                        // Find the position label from the *full* slot list using the ID
                        // Ensure we check for `id` presence as SUB slots don't have it
                        const slotInfo = allAvailableSlotsForLookup.find(slot => slot.id != null && slot.id === Number(assignedSlotId));
                        if (slotInfo) {
                            assignedPosLabel = slotInfo.position; // Use the XI position label
                        } else {
                            console.warn(`[PlayerList Effect] Could not find slot info for assigned slot ID: ${assignedSlotId}`);
                            // Keep assignedPosLabel as '-' if slot lookup fails
                        }
                    }
                }

                return {
                    ...p,
                    id: p._id || p.wyId, // Ensure unique ID for React keys
                    rating: p.averageRating != null ? p.averageRating.toFixed(1) : '-',
                    tacticalPosition: assignedPosLabel, // Display label based on assignment (Sub > XI > '-')
                    placed: assignedPosLabel !== '-', // Mark as placed if assigned to anything other than '-'
                    primaryPosition: p.position 
                        || p.role?.code3 
                        || p.role?.code2 
                        || p.role?.name 
                        || '-',
                }
            });

            // Sorting logic (uses tacticalPosition label and sort map)
            processedPlayers.sort((a, b) => {
                const posA = a.tacticalPosition;
                const posB = b.tacticalPosition;

                // Determine sort order: Use XI map for XI positions, high number for subs/unassigned
                const orderA = posA.startsWith('SUB - ') ? 900 : (currentFormationSortMap[posA] ?? 999); // Subs sort together
                const orderB = posB.startsWith('SUB - ') ? 900 : (currentFormationSortMap[posB] ?? 999); // Unassigned ('-') get 999

                if (orderA !== orderB) {
                    return orderA - orderB; // Sort by position order (XI first, then Subs, then Unassigned)
                }

                // Optional: Add secondary sort for subs (alphabetical) and unassigned
                 if (orderA === 900) { // Both are Subs
                     return posA.localeCompare(posB); // Sort subs alphabetically
                 }
                 if (orderA === 999) { // Both are Unassigned
                     // Sort unassigned by name, for example
                     return (a.name ?? '').localeCompare(b.name ?? '');
                 }


                return 0; // Maintain relative order for same XI positions
            });

            setLocalPlayersList(processedPlayers);
        } else {
            setLocalPlayersList([]);
        }
    // Update dependencies: Include the new substitute map
    }, [playersApiResponse, assignedSlotsMap, assignedSubstitutesMap, currentFormationSortMap, currentFormationSlots]);

    // Combine loading states (removed isFormationsLoading)
    const isLoading = isTeamsLoading || isTeamLoading || isPlayersLoading || isTeamSheetLoading;
    // Combine error states (removed formationsError)
    const combinedError = teamsError || teamError || playersError || teamSheetError;


    // Function to reorder players locally (for drag/drop or sorting) - unchanged
    const moveItem = (index, newIndex) => {
        if (newIndex < 0 || newIndex >= localPlayersList.length || index === newIndex) {
            return; // Prevent invalid moves
        }

        setLocalPlayersList(prevList => {
            const itemToMove = prevList[index];
            const newList = [...prevList];
            newList.splice(index, 1); // Remove item from original position
            newList.splice(newIndex, 0, itemToMove); // Insert item at new position
            return newList;
        });
    };


    // Open/Close position dropdown - unchanged
    const openPositionDropdown = (player) => {
        console.log(`Opening position dropdown for player: ${player?.name} (wyId: ${player?.wyId})`);
        setPlayerForPositionChange(player);
    };

    const closePositionDropdown = () => {
        console.log("Closing position dropdown");
        setPlayerForPositionChange(null);
    };


    const handlePlayerSwap = (playerA_wyId, playerB_wyId) => {
        console.log(`[SWAP] Swapping player ${playerA_wyId} with ${playerB_wyId}`);
        const newSlotsMap = { ...assignedSlotsMap };
        const newSubsMap = { ...assignedSubstitutesMap };

        const posA = newSlotsMap[playerA_wyId] || newSubsMap[playerA_wyId] || null;
        const posB = newSlotsMap[playerB_wyId] || newSubsMap[playerB_wyId] || null;

        // Simple swap logic needed here.
        // For now, let's just log and see if we get the right players.
        // A full implementation will need to find the players in the maps and swap their values.

        // A simplified conceptual swap:
        // 1. Find where A and B are (XI or Subs)
        const slotA = Object.keys(newSlotsMap).find(key => newSlotsMap[key] === playerA_wyId);
        const subA_pos = newSubsMap[playerA_wyId];

        const slotB = Object.keys(newSlotsMap).find(key => newSlotsMap[key] === playerB_wyId);
        const subB_pos = newSubsMap[playerB_wyId];

        // 2. Remove both from their current positions
        if (slotA) delete newSlotsMap[slotA];
        if (subA_pos) delete newSubsMap[playerA_wyId];
        if (slotB) delete newSlotsMap[slotB];
        if (subB_pos) delete newSubsMap[playerB_wyId];

        // 3. Place them in each other's old positions
        if (slotA) newSlotsMap[slotA] = playerB_wyId;
        if (subA_pos) newSubsMap[playerB_wyId] = subA_pos;
        if (slotB) newSlotsMap[slotB] = playerA_wyId;
        if (subB_pos) newSubsMap[playerA_wyId] = subB_pos;

        setAssignedSlotsMap(newSlotsMap);
        setAssignedSubstitutesMap(newSubsMap);
        closePositionDropdown();
    }

    // Handle position selection - Rewritten for Slots Map and Subs Map
    const handlePositionSelect = (wyIdToAssign, selectedValue) => {
        // Check if the selected value is another player's wyId (a swap)
        if (typeof selectedValue === 'number' || !isNaN(Number(selectedValue))) {
            handlePlayerSwap(wyIdToAssign, Number(selectedValue));
            return;
        }
        
        const selectedSlotObject = selectedValue;
        const selectedSlotId = selectedSlotObject.id; // ID of the XI slot (null/undefined for '-' or 'SUB - ...')
        const selectedPositionLabel = selectedSlotObject.position; // Label ('-', 'GK', 'CB', 'SUB - ST', etc.)
        console.log(`[Handler] Assigning Player ${wyIdToAssign} to Position: ${selectedPositionLabel} (Slot ID: ${selectedSlotId ?? 'N/A'})`);

        // Create copies of state maps to modify
        const newSlotsMap = { ...assignedSlotsMap };
        const newSubsMap = { ...assignedSubstitutesMap };

        // --- Step 1: Remove the player being assigned from *any* previous assignment ---
        let previousSlotId = null;
        for (const [slotId, playerId] of Object.entries(newSlotsMap)) {
            if (playerId === wyIdToAssign) {
                previousSlotId = slotId;
                break;
            }
        }
        if (previousSlotId) {
            console.log(` -> Removing player ${wyIdToAssign} from previous XI slot ${previousSlotId}`);
            delete newSlotsMap[previousSlotId];
        }
        if (newSubsMap[wyIdToAssign]) {
            console.log(` -> Removing player ${wyIdToAssign} from previous substitute role ${newSubsMap[wyIdToAssign]}`);
            delete newSubsMap[wyIdToAssign];
        }

        // --- Step 2: Handle the new assignment based on selectedPositionLabel ---
        if (selectedPositionLabel === '-') {
            // Player removed - Already handled by Step 1, no further action needed.
             console.log(` -> Player ${wyIdToAssign} is now unassigned.`);

        } else if (selectedPositionLabel.startsWith('SUB - ')) {
            // Assigning to a Substitute role
             console.log(` -> Assigning player ${wyIdToAssign} as Substitute: ${selectedPositionLabel}`);
            newSubsMap[wyIdToAssign] = selectedPositionLabel;

        } else if (selectedSlotId != null) {
            // Assigning to a Starting XI slot
            const currentOccupantWyId = newSlotsMap[selectedSlotId]; // Who is currently in the target slot?

            if (currentOccupantWyId === wyIdToAssign) {
                 console.log(` -> Clicked own slot ${selectedSlotId}. No change needed.`);
                 // Re-add the player to their slot (effectively no change, already removed in step 1)
                 newSlotsMap[selectedSlotId] = wyIdToAssign;
            } else if (currentOccupantWyId != null) {
                 // Target slot is occupied by someone else - Make the occupant unassigned
                 console.log(` -> Slot ${selectedSlotId} occupied by ${currentOccupantWyId}. Making them unassigned.`);
                 // Remove the displaced player from any substitute role they might have held
                 if (newSubsMap[currentOccupantWyId]) {
                     console.log(`   -> Removing displaced player ${currentOccupantWyId} from substitute role ${newSubsMap[currentOccupantWyId]}`);
                     delete newSubsMap[currentOccupantWyId];
                 }
                 // Assign the new player to the slot (occupant already removed from this slot implicitly as they are now unassigned)
                 console.log(` -> Assigning player ${wyIdToAssign} to slot ${selectedSlotId}.`);
                 newSlotsMap[selectedSlotId] = wyIdToAssign;
            } else {
                 // Target slot is empty - Simply assign
                 console.log(` -> Assigning player ${wyIdToAssign} to empty slot ${selectedSlotId}.`);
                 newSlotsMap[selectedSlotId] = wyIdToAssign;
            }
        } else {
            console.error(`[Handler] Error: Invalid selection state. Label: ${selectedPositionLabel}, Slot ID: ${selectedSlotId}`);
        }


        // --- Step 3: Update state ---
        setAssignedSlotsMap(newSlotsMap);
        setAssignedSubstitutesMap(newSubsMap);
        closePositionDropdown();
    };


    // Handle formation change - Remaps players instead of resetting
    const handleFormationChange = (newFormationId) => {
        if (newFormationId === activeFormationId) return;

        const newFormation = formationsData.find(f => f.id === newFormationId);
        if (!newFormation || !newFormation.positions) {
            setActiveFormationId(newFormationId);
            setAssignedSlotsMap({});
            setAssignedSubstitutesMap({});
            return;
        }

        // Preserve players from the starting XI and remap them
        const currentXIPlayerIds = Object.values(assignedSlotsMap);
        const newSlotsMap = {};
        newFormation.positions.forEach((slot, index) => {
            const playerId = currentXIPlayerIds[index];
            if (playerId) {
                newSlotsMap[slot.id] = playerId;
            }
        });

        // Check substitutes and un-assign any whose position is no longer valid
        const newFormationSubPositions = new Set(newFormation.positions.map(p => `SUB - ${p.position}`));
        const newSubsMap = { ...assignedSubstitutesMap };
        Object.keys(newSubsMap).forEach(playerId => {
            if (!newFormationSubPositions.has(newSubsMap[playerId])) {
                delete newSubsMap[playerId];
            }
        });

        // Update state
        setActiveFormationId(newFormationId);
        setAssignedSlotsMap(newSlotsMap);
        setAssignedSubstitutesMap(newSubsMap);
    };

    // Handle Save Teamsheet - Update payload creation to include subs
    const handleSaveTeamsheet = () => {
        console.log("[TeamPageList] Attempting to save teamsheet...");

        // 1. Prepare Starting XI players
        const playersToSave = Object.entries(assignedSlotsMap).map(([slotId, wyId]) => {
            // Find the original slot object to get the position label
            // Look in the *full* list including subs, but filter by ID presence
            const slotInfo = currentFormationSlots.find(slot => slot.id != null && slot.id === Number(slotId));
            const positionLabel = slotInfo?.position || 'Unknown'; // Fallback
            if (positionLabel === 'Unknown') {
                 console.warn(`[Save] Could not find position label for starting slot ID ${slotId}`);
            }
            return {
                wyId: Number(wyId),
                position: positionLabel, // Save the XI label (e.g., 'CB', 'ST')
            };
        });

         // 2. Prepare Substitute players
         const substitutesToSave = Object.entries(assignedSubstitutesMap).map(([wyId, positionLabel]) => {
             // Basic validation that it's actually a SUB position being saved
             if (!positionLabel.startsWith('SUB - ')) {
                 console.warn(`[Save] Non-SUB position found in substitutes map for player ${wyId}: ${positionLabel}. Skipping.`);
                 return null; // Skip this entry
             }
             // Extract the base position by removing the prefix
             const basePosition = positionLabel.substring(6); // "SUB - ST" -> "ST"

             return {
                 wyId: Number(wyId),
                 position: basePosition, // Save only the base position
             };
         }).filter(sub => sub !== null); // Filter out any skipped entries

        // 3. Get Formation Title (unchanged)
        const selectedFormation = findObjectByKeyValue(formationsData || [], 'id', activeFormationId);
        const currentFormationTitle = selectedFormation?.title;

        // 4. Get Tactic ID Character (unchanged)
        const selectedTactic = findObjectByKeyValue(tacticData || [], 'id', activeTacticId);
        const currentTacticTitle = selectedTactic?.title;
        const tacticForwardMapping = {
            "Attacking": "A", "Neutral": "N", "Defensive": "D",
            "Counterattacking": "C", "Passing": "P", "Long Ball": "L"
        }; // Ensure this mapping is complete
        const currentTacticIdChar = currentTacticTitle ? tacticForwardMapping[currentTacticTitle] : null;

        // 5. Validation (unchanged, but could add more checks)
        if (!currentFormationTitle) {
            toast.error("Cannot save: Please select a valid formation."); return;
        }
        if (!currentTacticIdChar) { // Check if a tactic is selected and mapped
             toast.error("Cannot save: Please select a valid tactic."); return;
        }
         // Example: Check if starting XI has 11 players (optional)
         // if (playersToSave.length !== 11) {
         //    toast.warn(`Saving with ${playersToSave.length} starting players.`);
         // }

        // 6. Construct Payload - include subs array
        const teamsheetPayload = {
            formation: currentFormationTitle,
            tacticId: currentTacticIdChar,
            players: playersToSave,      // Starting XI
            subs: substitutesToSave      // Substitutes
        };
        console.log("Saving teamsheet payload:", teamsheetPayload);

        // 7. Call Mutation wrapped in toast.promise for unified UX feedback
        toast.promise(
          new Promise((resolve, reject) => {
            saveTeamsheetMutate(
              { teamId: myTeamId, teamsheetData: teamsheetPayload },
              {
                onSuccess: (data) => resolve(data),
                onError: (error) => reject(error)
              }
            );
          }),
          {
            loading: 'Saving teamsheet…',
            success: 'Teamsheet saved successfully!',
            error: (err) => {
              const errorMsg = err?.response?.data?.message || err.message || 'Failed to save teamsheet.';
              return `Save failed: ${errorMsg}`;
            },
          }
        ).then(() => {
          isInitializedRef.current = false; // Allow re-initialization on next load if needed
          setIsSuccessPopupOpen(true);
        });
    };

    // --- JSX Return ---
    return (
        <div className="page-index">
            <Popup popOpen={isSuccessPopupOpen} close={() => setIsSuccessPopupOpen(false)}>
                <p style={{ padding: '20px', textAlign: 'center' }}>Team successfully saved</p>
            </Popup>
            <div className="wrapper">
                {/* TeamPageNav */}
                <TeamPageNav id={myTeamId} />

                <div className="list-grid">
                    {/* Settings Column */}
                    <div className={`settings-col m-filters-col ${activeSettings ? 'active' : ''}`}>
                        <div className="m-filters-head">
                            <h3>Tactics</h3>
                            <button className="filters-close-btn icon-s-cross"
                                    onClick={() => { setActiveSettings(false); } } />
                        </div>

                        {/* Team Info Block */}
                        <div className="setting-block">
                             {!isTeamLoading && !teamError && teamData ? ( // Simplified check
                                <div className="grid-info-2">
                                    <div className="col">
                                        <h4>Team name</h4>
                                        <div className="info">{teamData?.data?.name || 'N/A'}</div>
                                    </div>
                                    <div className="col">
                                        <h4>Managed by</h4>
                                        <div className="info">
                                            {teamData?.data?.owner?.username ? (
                                                <Link to={`/user/${teamData.data.owner._id}`}>{teamData.data.owner.username}</Link> // Link to user profile if possible
                                            ) : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            ) : isTeamLoading ? (
                                <Spinner size="small"/>
                            ) : teamError ? (
                                <ErrorMessage message="Error loading team details"/>
                            ) : null }
                        </div>

                        {/* Formation & Tactic Block */}
                        <div className="setting-block">
                            {/* Formation Selector */}
                            <div className="drop-container">
                                <h4>Select formation</h4>
                                 {/* Add loading state check */}
                                 {isTeamSheetLoading ? <Spinner size="small"/> :
                                     <TeamDropdown
                                        props={formationsData || []}
                                        activeItemId={activeFormationId}
                                        setId={handleFormationChange} // This now resets both maps
                                     />
                                 }
                            </div>
                            {/* Tactic Dropdown */}
                            <div className="drop-container">
                                <h4>Select tactic</h4>
                                {/* Improved conditional rendering for Tactic Dropdown */}
                                {isTeamSheetLoading ? <Spinner size="small"/> : // Show spinner while loading sheet
                                 teamSheetError ? <ErrorMessage message="Error loading tactic"/> : // Show error if sheet fails
                                 !activeTacticId && tacticData?.length > 0 ? <div className="info">Select tactic</div> : // Prompt if no tactic selected
                                    <TeamDropdown
                                        props={tacticData || []}
                                        activeItemId={activeTacticId}
                                        setId={setActiveTacticId}
                                    />
                                }
                            </div>
                        </div>

                         {/* Current Position / Overall Rank Block - Placeholder */}
                         <div className="setting-block">
                            <div className="grid-info-2">
                                <div className="col">
                                    <h4>Current position</h4>
                                    <div className="info">N/A</div>
                                </div>
                                <div className="col">
                                    <h4>Overall rank</h4>
                                    <div className="info">N/A</div>
                                </div>
                            </div>
                        </div>

                        {/* View Team Pitch Button */}
                        {/*<Link to={activeLeague ? `/${activeLeague.slug}/team-pitch/${myTeamId}` : '#'} className="submit-btn">VIEW TEAM PITCH</Link>*/}

                        {/* Team Settings Button */}
                        <Link to={activeLeague ? `/${activeLeague.slug}/team-settings/${myTeamId}` : '#'} className="settings-submit-btn">TEAM SETTINGS</Link>

                        <div className="desktop-block">
                            {/* Save Teamsheet Button */}
                            <button
                                type="button"
                                className="settings-submit-btn"
                                onClick={handleSaveTeamsheet}
                                disabled={isSavingTeamsheet || isLoading} // Also disable if initial data is loading
                                style={{ marginTop: '10px' }}
                            >
                                {isSavingTeamsheet ? 'Saving...' : 'Save Teamsheet'}
                            </button>
                            {/* Save Error Display */}
                            {saveError && (
                                <div style={{ color: 'red', marginTop: '10px', fontSize: '0.9em' }}>
                                    Error: {saveError?.response?.data?.message || saveError.message || 'An unknown error occurred'}
                                </div>
                            )}

                        </div>

                    </div>

                    {/* Player List Column */}
                    <div className="l-players-col">
                        <TutorialTooltip
                          id="squad-list-tooltip"
                          title="Squad list view"
                          description="Here you can edit your first team and substitutes as an alternative to the pitch view, as well as set tactics and view some basic player stats."
                          className="f-right"
                        /> 

                        <div className="players-list">
                            {/* Table Head */}
                            <TableHead />

                             {/* Main Content: Loading / Error / Empty / PlayerItems */}
                             {isLoading ? ( // Use combined isLoading
                                <div className="loading-container"><Spinner /><p>Loading data...</p></div>
                             ) : combinedError ? (
                                <ErrorMessage message={combinedError?.response?.data?.message || combinedError.message || "Error loading team data"} />
                             ) : localPlayersList.length === 0 && (!playersApiResponse?.data || playersApiResponse.data.length === 0) ? (
                                <div className="empty-state">
                                    <p>No players found for this team.</p>
                                    {/* Maybe add link to import players */}
                                </div>
                             ) : (
                                <PlayerItems
                                    props={localPlayersList} // Now includes players with 'SUB - ...' tacticalPosition
                                    moveItem={moveItem}
                                    // Pass down necessary props for the dropdown
                                    assignedSlotsMap={assignedSlotsMap} // Pass down starting XI map
                                    assignedSubstitutesMap={assignedSubstitutesMap} // Pass down substitute map
                                    currentFormationSlots={currentFormationSlots} // Pass the combined list (XI + Subs + Remove)
                                    playerForPositionChange={playerForPositionChange}
                                    openPositionDropdown={openPositionDropdown}
                                    closePositionDropdown={closePositionDropdown}
                                    handlePositionSelect={handlePositionSelect} // Pass the updated handler
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="mobile-blocks-container">
                    {/* Save Teamsheet Button */}
                    <button
                        type="button"
                        className="settings-submit-btn"
                        onClick={handleSaveTeamsheet}
                        disabled={isSavingTeamsheet || isLoading} // Also disable if initial data is loading
                        style={{ marginTop: '10px' }}
                    >
                        {isSavingTeamsheet ? 'Saving...' : 'Save Teamsheet'}
                    </button>
                    {/* Save Error Display */}
                    {saveError && (
                        <div style={{ color: 'red', marginTop: '10px', fontSize: '0.9em' }}>
                            Error: {saveError?.response?.data?.message || saveError.message || 'An unknown error occurred'}
                        </div>
                    )}
                </div>



                {/* Mobile tactics bar, docked above the bottom navigation */}
                <div className="filters-btn-block">
                    <button type="button" className="filters-btn"
                            onClick={() => { setActiveSettings(true); } }>
                        <span className="ico icon-filters"></span>
                        TACTICS
                    </button>
                </div>
            </div>
         </div>
     );
 }

export { MyTeamList };